import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import AIChatWidget from './AIChatWidget';
import Footer from './Footer';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from './ui/badge';
import { DollarSign, Home, Package, Flame, ShoppingCart, Store, Rocket, Search, X } from 'lucide-react';

function SaleCelebration({ sale, onClose }) {
  useEffect(() => {
    // Play celebration sound
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [523.25, 659.25, 783.99, 1046.50].forEach((f, i) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination); o.frequency.value = f; o.type = 'sine';
        g.gain.setValueAtTime(0.12, ctx.currentTime + i * 0.15);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.15 + 0.4);
        o.start(ctx.currentTime + i * 0.15); o.stop(ctx.currentTime + i * 0.15 + 0.4);
      });
    } catch (e) {}
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, []);

  const colors = ['#f59e0b','#10b981','#3b82f6','#ec4899','#8b5cf6','#FFD700'];
  return (
    <>
      {/* Confetti */}
      <div className="fixed inset-0 z-[100] pointer-events-none overflow-hidden">
        {Array.from({length:40}).map((_,i) => (
          <motion.div key={i} className="absolute w-2.5 h-2.5 rounded-sm"
            style={{backgroundColor:colors[i%colors.length],left:`${Math.random()*100}%`,top:'-10px'}}
            initial={{y:-20,rotate:0,opacity:1}}
            animate={{y:window.innerHeight+50,rotate:Math.random()*720-360,x:(Math.random()-0.5)*300,opacity:[1,1,0]}}
            transition={{duration:2+Math.random(),delay:Math.random()*0.4,ease:'easeIn'}}
          />
        ))}
      </div>
      {/* Toast banner */}
      <motion.div
        initial={{ y: -100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -100, opacity: 0 }}
        className="fixed top-4 left-1/2 -translate-x-1/2 z-[99] pointer-events-auto"
        onClick={onClose}
      >
        <div className="bg-gradient-to-r from-primary to-emerald-500 text-white rounded-2xl px-6 py-4 shadow-2xl shadow-primary/30 flex items-center gap-4 min-w-[320px]">
          <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1 }} className="text-4xl">🎉</motion.div>
          <div>
            <p className="font-bold text-sm">{sale.isRTL ? 'مبروك! تم بيع منتج!' : 'Congrats! Product Sold!'}</p>
            <p className="text-xs opacity-90">{sale.productName}</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge className="bg-white/20 text-white border-0 text-[10px]">+${sale.profit}</Badge>
              <span className="text-[10px] opacity-80">{sale.buyerName} — {sale.country}</span>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === 'true');
  const { isRTL } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [saleCelebration, setSaleCelebration] = useState(null);
  const [lastOrderCount, setLastOrderCount] = useState(null);
  const isProductsPage = location.pathname === '/products' || location.pathname.startsWith('/products/');
  const [showPromoPopup, setShowPromoPopup] = useState(false);

  // Show promo popup after 4 seconds (only once per session)
  useEffect(() => {
    if (sessionStorage.getItem('promo_shown')) return;
    const t = setTimeout(() => { setShowPromoPopup(true); sessionStorage.setItem('promo_shown', '1'); }, 4000);
    return () => clearTimeout(t);
  }, []);

  // Poll for new orders every 30 seconds to show celebration
  useEffect(() => {
    if (!isAuthenticated || !user?._id) return;
    const check = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/my?user_id=${user._id}&limit=1`);
        const d = await r.json();
        const count = d.total || 0;
        if (lastOrderCount !== null && count > lastOrderCount && d.orders?.length) {
          const o = d.orders[0];
          setSaleCelebration({
            productName: isRTL ? o.product_name_ar : o.product_name,
            profit: o.profit || o.commission || 0,
            buyerName: o.buyer?.name || '',
            country: isRTL ? o.buyer?.country_ar : o.buyer?.country || '',
            isRTL
          });
        }
        setLastOrderCount(count);
      } catch (e) {}
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, user?._id, lastOrderCount]);

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('sidebar_collapsed', String(next));
  };

  const marginClass = collapsed
    ? (isRTL ? 'mr-0 lg:mr-16' : 'ml-0 lg:ml-16')
    : (isRTL ? 'mr-0 lg:mr-72' : 'ml-0 lg:ml-72');

  // When sidebar is collapsed, allow content to expand more
  const maxWidthClass = collapsed ? 'max-w-[1600px]' : 'max-w-[1200px]';

  return (
    <div className="min-h-screen bg-background noise-bg overflow-x-hidden">
      {/* Promo Popup */}
      <AnimatePresence>
        {showPromoPopup && <PromoPopup isRTL={isRTL} onClose={() => setShowPromoPopup(false)} navigate={navigate} />}
      </AnimatePresence>
      <AnimatePresence>
        {saleCelebration && <SaleCelebration sale={saleCelebration} onClose={() => setSaleCelebration(null)} />}
      </AnimatePresence>
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={collapsed}
        onToggleCollapse={toggleCollapse}
      />
      {/* Desktop Fixed Header */}
      <div 
        className={`hidden lg:block fixed top-0 z-50 transition-all duration-300`}
        style={{ 
          left: isRTL ? 0 : (collapsed ? '4rem' : '18rem'), 
          right: isRTL ? (collapsed ? '4rem' : '18rem') : 0 
        }}>
        <Topbar onMenuClick={() => setSidebarOpen(true)} />
      </div>
      {/* Mobile Header (fixed) */}
      <div className={`lg:hidden fixed top-0 left-0 right-0 z-50`}>
        <Topbar onMenuClick={() => setSidebarOpen(true)} />
        {/* Mobile Search Bar */}
        <MobileSearchBar isRTL={isRTL} />
      </div>
      {/* Main Content - with top padding for fixed header + search */}
      <div className={`transition-all duration-300 ${marginClass} pt-[7.5rem] lg:pt-16`}>
        <main className={`p-4 sm:p-6 lg:p-8 ${maxWidthClass} mx-auto pb-24 lg:pb-8 transition-all duration-300`}>
          <Outlet />
        </main>
        {!isProductsPage && <Footer />}
      </div>
      {!isProductsPage && <AIChatWidget />}
      <MobileBottomNav isRTL={isRTL} />
    </div>
  );
}


// Mobile Search Bar with animated placeholder
const SEARCH_HINTS_AR = [
  'حقيبة يد نسائية', 'نظارات شمسية رجالية', 'هاتف محمول ذكي', 'مستحضرات تجميل نسائية',
  'ساعة يد فاخرة', 'حذاء رياضي رجالي', 'سماعات بلوتوث لاسلكية', 'حقيبة ظهر للسفر',
  'عطر نسائي فاخر', 'ملابس رياضية نسائية', 'إكسسوارات سيارات', 'ألعاب أطفال تعليمية',
  'مستلزمات حيوانات أليفة', 'أدوات مطبخ حديثة', 'ديكور منزلي عصري', 'معدات رياضية',
  'حقيبة لابتوب جلدية', 'مجوهرات ذهبية', 'كريم عناية بالبشرة', 'حذاء كلاسيكي رجالي',
  'شاحن لاسلكي سريع', 'وسائد ديكور ملونة', 'قبعة شمسية نسائية', 'محفظة جلدية رجالية',
  'فرشاة مكياج احترافية', 'سكوتر كهربائي أطفال', 'مصباح مكتب LED', 'غلاية كهربائية',
  'حقيبة سهرة ذهبية', 'كاميرا مراقبة ذكية',
];
const SEARCH_HINTS_EN = [
  'Women handbag', 'Men sunglasses', 'Smartphone', 'Cosmetics',
  'Luxury watch', 'Running shoes', 'Wireless earbuds', 'Travel backpack',
  'Women perfume', 'Activewear', 'Car accessories', 'Kids toys',
  'Pet supplies', 'Kitchen tools', 'Home decor', 'Fitness gear',
  'Laptop bag', 'Gold jewelry', 'Skincare cream', 'Dress shoes',
  'Wireless charger', 'Throw pillows', 'Beach hat', 'Leather wallet',
  'Makeup brushes', 'Kids scooter', 'Desk lamp', 'Electric kettle',
  'Evening clutch', 'Security camera',
];

function MobileSearchBar({ isRTL }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [focused, setFocused] = useState(false);
  const [hintIdx, setHintIdx] = useState(0);
  const hints = isRTL ? SEARCH_HINTS_AR : SEARCH_HINTS_EN;
  const debounceRef = React.useRef(null);

  useEffect(() => {
    const t = setInterval(() => setHintIdx(p => (p + 1) % hints.length), 4000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (query.length < 1) { setResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products?search=${encodeURIComponent(query)}&limit=8`);
        const d = await r.json();
        setResults(d.products || []);
      } catch { setResults([]); }
    }, 200);
  }, [query]);

  const addToCart = (p) => {
    try {
      const cart = JSON.parse(localStorage.getItem('ss_cart') || '[]');
      const existing = cart.find(c => c.id === (p.id || p.product_id));
      const newCart = existing ? cart.map(c => c.id === (p.id || p.product_id) ? { ...c, qty: c.qty + 1 } : c) : [...cart, { ...p, id: p.id || p.product_id, qty: 1 }];
      localStorage.setItem('ss_cart', JSON.stringify(newCart));
      localStorage.setItem('ss_cart_bounce', Date.now().toString());
      window.dispatchEvent(new Event('storage'));
    } catch {}
  };

  return (
    <div className="bg-card/95 backdrop-blur-md border-b border-border/50 px-3 py-2 relative">
      <div className="relative">
        <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10 ${isRTL ? 'right-3' : 'left-3'}`} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 200)}
          placeholder={hints[hintIdx]}
          className={`w-full h-10 ${isRTL ? 'pr-10 pl-8' : 'pl-10 pr-8'} bg-secondary/50 border border-border rounded-xl text-sm placeholder:text-muted-foreground/60 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all`}
          data-testid="mobile-search-input"
        />
        {query && (
          <button onClick={() => { setQuery(''); setResults([]); }}
            className={`absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-muted flex items-center justify-center ${isRTL ? 'left-2' : 'right-2'}`}>
            <X className="w-3 h-3 text-muted-foreground" />
          </button>
        )}
      </div>
      {/* Results dropdown */}
      {focused && results.length > 0 && (
        <div className="absolute left-0 right-0 top-full bg-card border-b border-border shadow-xl z-50 max-h-80 overflow-y-auto">
          {results.map((p, i) => (
            <div key={p.id || i} className="flex items-center gap-3 px-4 py-2.5 border-b border-border/30 hover:bg-secondary/50 active:bg-secondary cursor-pointer"
              onClick={() => { setQuery(''); setResults([]); navigate(`/product/${p.id || p.product_id}`); }}>
              <img src={p.image} alt="" className="w-12 h-12 rounded-lg object-cover ring-1 ring-border/30 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-foreground truncate">{isRTL ? p.name_ar : p.name}</p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-sm font-bold text-primary font-mono">${p.sell_price}</span>
                  {p.original_price > 0 && p.sell_price < p.original_price && (
                    <span className="text-[10px] text-muted-foreground line-through">${p.original_price}</span>
                  )}
                </div>
              </div>
              <button className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center border border-primary/20"
                onClick={(e) => { e.stopPropagation(); addToCart(p); }}>
                <ShoppingCart className="w-3.5 h-3.5 text-primary" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Mobile Bottom Navigation Bar

const PROMO_IMG = 'https://static.prod-images.emergentagent.com/jobs/e3270ad9-5cfd-409f-aebd-1e1797181d3d/images/66f85b3d972e17a8d13720678f3aebe36bf2c266c74a11bbbcdfa934c618e5fc.png';

function PromoPopup({ isRTL, onClose, navigate }) {
  const dollars = Array.from({length:5}).map((_,i) => ({
    left: Math.random()*100, delay: Math.random()*2, dur: 2+Math.random()*2, size: 16+Math.random()*20
  }));

  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{scale:0.5,y:80,opacity:0}} animate={{scale:1,y:0,opacity:1}} exit={{scale:0.8,y:40,opacity:0}}
        transition={{type:'spring',stiffness:300,damping:20}}
        className="relative max-w-lg w-full rounded-3xl overflow-hidden shadow-2xl" onClick={e=>e.stopPropagation()}>
        
        {/* Flying Dollars Animation */}
        <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden">
          {dollars.map((d,i)=>(
            <motion.div key={i} className="absolute text-2xl" style={{left:`${d.left}%`,fontSize:d.size}}
              initial={{y:'110%',opacity:0,rotate:Math.random()*60-30}}
              animate={{y:'-20%',opacity:[0,1,1,0],rotate:Math.random()*60-30}}
              transition={{repeat:Infinity,duration:d.dur,delay:d.delay,ease:'linear'}}>
              💵
            </motion.div>
          ))}
        </div>

        {/* Close */}
        <button onClick={onClose} className="absolute top-3 right-3 z-30 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 text-lg">&times;</button>

        {/* Image */}
        <div className="relative h-52 overflow-hidden">
          <img src={PROMO_IMG} alt="" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-transparent to-transparent" />
          {/* Logo */}
          <div className="absolute top-3 left-3 z-20 bg-white/90 rounded-xl px-3 py-1.5 shadow-lg">
            <span className="font-bold text-sm bg-gradient-to-r from-primary to-emerald-500 bg-clip-text text-transparent">S&S SHOP & SHIP</span>
          </div>
        </div>

        {/* Content */}
        <div className="bg-gradient-to-b from-[#0f172a] to-[#1e293b] px-6 pb-6 pt-2 relative z-20">
          <motion.h2 initial={{y:20,opacity:0}} animate={{y:0,opacity:1}} transition={{delay:0.3}}
            className="text-2xl sm:text-3xl font-bold text-white text-center leading-tight mb-3">
            {isRTL ? 'فرصة العمر!' : 'Opportunity of a Lifetime!'}
            <br />
            <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
              {isRTL ? 'كن تاجراً أونلاين الآن!' : 'Become an Online Seller Now!'}
            </span>
          </motion.h2>
          
          <motion.p initial={{y:20,opacity:0}} animate={{y:0,opacity:1}} transition={{delay:0.4}}
            className="text-white/70 text-sm text-center mb-4">
            {isRTL 
              ? 'أسهل وأقوى منصة تجارة إلكترونية — بدون مخزون، بدون شحن، بدون خبرة. فقط اختر منتجاتك وابدأ البيع!'
              : 'The easiest e-commerce platform — no inventory, no shipping, no experience. Just pick products and start selling!'}
          </motion.p>

          <motion.div initial={{y:20,opacity:0}} animate={{y:0,opacity:1}} transition={{delay:0.5}}
            className="flex items-center justify-center gap-4 mb-5">
            {[
              { icon: '🚚', text: isRTL ? 'شحن مجاني' : 'Free Shipping' },
              { icon: '💰', text: isRTL ? 'عمولة 20%' : '20% Commission' },
              { icon: '🏪', text: isRTL ? 'متجرك الخاص' : 'Your Own Store' },
            ].map((f,i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <span className="text-xl">{f.icon}</span>
                <span className="text-[10px] text-white/60 font-medium">{f.text}</span>
              </div>
            ))}
          </motion.div>

          <motion.div initial={{y:20,opacity:0}} animate={{y:0,opacity:1}} transition={{delay:0.6}}>
            <motion.div whileHover={{scale:1.03}} whileTap={{scale:0.97}}>
              <button onClick={() => { onClose(); navigate('/register'); }}
                className="w-full h-14 rounded-2xl bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 text-black font-bold text-lg shadow-xl shadow-amber-500/30 flex items-center justify-center gap-3 hover:from-amber-500 hover:to-yellow-500 transition-all">
                <Rocket className="w-6 h-6" />
                {isRTL ? 'سجّل مجاناً وابدأ الآن!' : 'Sign Up Free & Start Now!'}
              </button>
            </motion.div>
          </motion.div>

          <p className="text-center text-white/40 text-xs mt-3 cursor-pointer hover:text-white/60" onClick={onClose}>
            {isRTL ? 'لا شكراً، أريد التسوق فقط' : 'No thanks, just browsing'}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

function MobileBottomNav({ isRTL }) {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const check = () => { try { setCartCount(JSON.parse(localStorage.getItem('ss_cart') || '[]').reduce((s, c) => s + (c.qty || 0), 0)); } catch {} };
    check();
    window.addEventListener('storage', check);
    return () => { window.removeEventListener('storage', check); };
  }, []);

  const items = isRTL ? [
    { path: '/', icon: Home, label: 'الرئيسية' },
    { path: '/products', icon: Package, label: 'المنتجات' },
    { path: '/deals', icon: Flame, label: 'عروض اليوم', center: true },
    { path: '/cart', icon: ShoppingCart, label: 'السلة', badge: cartCount },
    { path: '/my-store', icon: Store, label: 'متجري' },
  ] : [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/products', icon: Package, label: 'Products' },
    { path: '/deals', icon: Flame, label: 'Deals', center: true },
    { path: '/cart', icon: ShoppingCart, label: 'Cart', badge: cartCount },
    { path: '/my-store', icon: Store, label: 'My Store' },
  ];

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-t border-border mobile-bottom-nav shadow-[0_-4px_20px_rgba(0,0,0,0.1)]">
      <div className="flex items-end justify-around px-2 pt-1 pb-2">
        {items.map((item, i) => {
          const Icon = item.icon;
          const isActive = path === item.path || (item.path !== '/' && path.startsWith(item.path));
          if (item.center) {
            return (
              <button key={i} onClick={() => navigate(item.path)} className="relative -mt-6 flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-xl shadow-red-500/30 ring-4 ring-card animate-mobile-pulse">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <span className="text-[10px] font-bold mt-1 text-red-500">{item.label}</span>
              </button>
            );
          }
          return (
            <button key={i} onClick={() => navigate(item.path)} className="flex flex-col items-center py-1 px-2 relative">
              <div className={`relative ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                <Icon className="w-5 h-5" />
                {item.badge > 0 && <span className="absolute -top-1.5 -right-2 w-4 h-4 bg-red-500 text-white rounded-full text-[8px] flex items-center justify-center font-bold">{item.badge}</span>}
              </div>
              <span className={`text-[10px] mt-0.5 ${isActive ? 'text-primary font-bold' : 'text-muted-foreground'}`}>{item.label}</span>
              {isActive && <div className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-primary rounded-full" />}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
